import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Checkbox, Heading, Link, Paragraph } from '@digdir/designsystemet-react';
import type { QuestionnaireFactor } from '../../../api/workEnvironmentTypes';
import { contactKey, contactText, ReportingChannels, sensitiveTextKey, whoSeesKey } from '../workEnvironmentFormView';
import classes from '../WorkEnvironmentFormSubmission.module.css';

type Props = {
  factor: QuestionnaireFactor;
  channels: ReportingChannels;
  skipped: boolean;
  onSkippedChange: (skipped: boolean) => void;
};

/** Someone who has experienced this may not feel safe answering, so they get another way to report it */
export const SensitivePartNotice = ({ factor, channels, skipped, onSkippedChange }: Props): ReactElement => {
  const { t } = useTranslation();
  const { whistleblowingUrl, followUpRoutineUrl } = channels;
  const contact = contactKey(channels);
  return (
    <Alert data-color="info" className={classes.sensitive}>
      <Heading level={3} data-size="2xs">
        {t('workEnvironmentFormSubmission.sensitive.title')}
      </Heading>
      <Paragraph data-size="sm">{t(`workEnvironmentFormSubmission.sensitive.${whoSeesKey(channels)}`)}</Paragraph>
      <Paragraph data-size="sm">
        {t(`workEnvironmentFormSubmission.sensitive.${sensitiveTextKey(factor.skippable, channels)}`)}
      </Paragraph>
      {contact && (
        <Paragraph data-size="sm">
          {t(`workEnvironmentFormSubmission.sensitive.${contact}`, { count: channels.safetyReps?.length ?? 0 })}{' '}
          {channels.safetyReps?.map((rep, index) => (
            <span key={rep.email}>
              {index > 0 && ', '}
              <Link href={`mailto:${rep.email}`} data-size="sm">
                {contactText(rep)}
              </Link>
            </span>
          ))}
        </Paragraph>
      )}
      {(whistleblowingUrl || followUpRoutineUrl) && (
        <div className={classes.links}>
          {whistleblowingUrl && (
            <Link href={whistleblowingUrl} target="_blank" rel="noopener" data-size="sm" data-color="neutral">
              {t('workEnvironmentFormSubmission.sensitive.whistleblowing')}
            </Link>
          )}
          {followUpRoutineUrl && (
            <Link href={followUpRoutineUrl} target="_blank" rel="noopener" data-size="sm" data-color="neutral">
              {t('workEnvironmentFormSubmission.sensitive.followUp')}
            </Link>
          )}
        </div>
      )}
      {factor.skippable && (
        <Checkbox
          data-size="sm"
          label={t('workEnvironmentFormSubmission.sensitive.skip')}
          checked={skipped}
          onChange={(event) => onSkippedChange(event.target.checked)}
        />
      )}
    </Alert>
  );
};
